import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

/**
 * HistoryTab — list of past batch runs with per-scenario drill-down.
 * Surfaces cleanup badges for live runs that need manual review.
 */
function HistoryTab({ practiceId }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRunId, setExpandedRunId] = useState(null);
  const [expandedScenarios, setExpandedScenarios] = useState([]);
  const [expandedScenarioId, setExpandedScenarioId] = useState(null);
  const [error, setError] = useState('');

  const toggleScenario = (scenarioId) => {
    setExpandedScenarioId(prev => prev === scenarioId ? null : scenarioId);
  };

  const downloadScenario = (sc) => {
    const blob = new Blob([JSON.stringify(sc, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `run-${sc.run_id}-${sc.scenario_key}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const load = useCallback(async () => {
    if (!practiceId) return;
    setLoading(true);
    try {
      const res = await api.request(`/practices/${practiceId}/voice-test/runs?limit=50`);
      setRuns(res.runs || []);
    } catch (err) {
      setError('Failed to load history: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [practiceId]);

  useEffect(() => { load(); }, [load]);

  const expand = async (runId) => {
    if (expandedRunId === runId) {
      setExpandedRunId(null);
      setExpandedScenarios([]);
      return;
    }
    try {
      const res = await api.request(`/practices/${practiceId}/voice-test/runs/${runId}`);
      setExpandedRunId(runId);
      setExpandedScenarios(res.scenarios || []);
    } catch (err) {
      setError('Failed to load run details: ' + err.message);
    }
  };

  const needsCleanup = runs.filter(r => r.cleanup_status === 'partial' || r.cleanup_status === 'failed' || r.cleanup_status === 'manual_review');

  return (
    <div style={S.container}>
      {error && <div style={S.err}>{error}</div>}

      {needsCleanup.length > 0 && (
        <div style={S.warnBanner}>
          ⚠ {needsCleanup.length} run{needsCleanup.length === 1 ? '' : 's'} need manual cleanup review — see docs/qa/manual-cleanup-needed.md
        </div>
      )}

      <div style={S.headerRow}>
        <h3 style={S.h3}>{runs.length} run{runs.length === 1 ? '' : 's'}</h3>
        <button style={S.btnSecondary} onClick={load}>Refresh</button>
      </div>

      {loading && <p>Loading...</p>}
      {!loading && runs.length === 0 && <p style={S.empty}>No runs yet.</p>}
      {runs.length > 0 && (
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Started</th>
              <th style={S.th}>Preset</th>
              <th style={S.th}>Mode</th>
              <th style={S.th}>Status</th>
              <th style={S.th} colSpan="3">PASS / PART / FAIL</th>
              <th style={S.th}>Cost</th>
              <th style={S.th}>Cleanup</th>
              <th style={S.th}></th>
            </tr>
          </thead>
          <tbody>
            {runs.map(r => (
              <React.Fragment key={r.id}>
                <tr>
                  <td style={S.td}>{new Date(r.started_at).toLocaleString()}</td>
                  <td style={S.td}>{r.preset || '(custom)'}</td>
                  <td style={S.td}>{r.mode === 'live' ? <span style={S.livePill}>live</span> : 'mock'}</td>
                  <td style={S.td}><span style={statusStyle(r.status)}>{r.status}</span></td>
                  <td style={{ ...S.td, textAlign: 'right', color: '#059669' }}>{r.pass_count}</td>
                  <td style={{ ...S.td, textAlign: 'right', color: '#d97706' }}>{r.partial_count}</td>
                  <td style={{ ...S.td, textAlign: 'right', color: '#dc2626' }}>{r.fail_count}</td>
                  <td style={{ ...S.td, textAlign: 'right' }}>${Number(r.cost_est_usd || 0).toFixed(2)}</td>
                  <td style={S.td}>{r.cleanup_status !== 'not_applicable' ? r.cleanup_status : '-'}</td>
                  <td style={S.td}><button style={S.btnSmall} onClick={() => expand(r.id)}>{expandedRunId === r.id ? 'Hide' : 'View'}</button></td>
                </tr>
                {expandedRunId === r.id && (
                  <tr>
                    <td colSpan="10" style={S.tdExpand}>
                      <b>Scenarios ({expandedScenarios.length}):</b>
                      <table style={S.innerTable}>
                        <thead><tr><th style={S.th}>Scenario</th><th style={S.th}>Verdict</th><th style={S.th}>Score</th><th style={S.th}>Duration</th><th style={S.th}>Turns</th><th style={S.th}>Tools</th><th style={S.th}></th></tr></thead>
                        <tbody>
                          {expandedScenarios.map(sc => (
                            <React.Fragment key={sc.id}>
                              <tr>
                                <td style={S.td}><b>{sc.scenario_key}</b></td>
                                <td style={S.td}><span style={verdictStyle(sc.verdict)}>{sc.verdict}</span></td>
                                <td style={S.td}>{sc.score ?? '-'}</td>
                                <td style={S.td}>{Math.round((sc.duration_ms || 0) / 1000)}s</td>
                                <td style={S.td}>{sc.turn_count ?? (parseTranscript(sc.transcript).length)}</td>
                                <td style={S.td}>{(parseTools(sc.actual_tools)).length}</td>
                                <td style={S.td}>
                                  <button style={S.btnSmall} onClick={() => toggleScenario(sc.id)}>
                                    {expandedScenarioId === sc.id ? 'Hide' : 'Transcript'}
                                  </button>
                                  <button style={S.btnSmall} onClick={() => downloadScenario(sc)}>JSON</button>
                                </td>
                              </tr>
                              {expandedScenarioId === sc.id && (
                                <tr>
                                  <td colSpan="7" style={S.tdDetail}>
                                    <ScenarioDetail sc={sc} />
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function parseTranscript(t) {
  if (!t) return [];
  if (Array.isArray(t)) return t;
  try { return JSON.parse(t); } catch { return []; }
}

function parseTools(t) {
  if (!t) return [];
  if (Array.isArray(t)) return t;
  try { return JSON.parse(t); } catch { return []; }
}

function ScenarioDetail({ sc }) {
  const rawTranscript = parseTranscript(sc.transcript);
  // Filter empty-text turns: Nova Sonic emits many contentStart/contentEnd
  // audio events that AutoTestRunner captures with text=''. They're not real
  // utterances — hide them so the conversation reads cleanly.
  const transcript = rawTranscript.filter(t => (t?.text || '').trim().length > 0);
  const hiddenCount = rawTranscript.length - transcript.length;
  const tools = parseTools(sc.actual_tools);
  const expected = parseTools(sc.expected_tools);

  return (
    <div style={SD.wrap}>
      <div style={SD.section}>
        <div style={SD.h4}>
          Conversation ({transcript.length} turns
          {hiddenCount > 0 && <span style={SD.empty}> · {hiddenCount} empty audio events hidden</span>})
        </div>
        {transcript.length === 0 && <div style={SD.empty}>(no transcript captured)</div>}
        <div style={SD.transcript}>
          {transcript.map((turn, i) => (
            <div key={i} style={{ ...SD.turn, ...(turn.role === 'assistant' ? SD.turnAssistant : SD.turnUser) }}>
              <div style={SD.turnRole}>{turn.role === 'assistant' ? 'Emily (AI)' : 'Patient (persona)'}</div>
              <div style={SD.turnText}>{turn.text}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={SD.section}>
        <div style={SD.h4}>Tool calls ({tools.length})</div>
        {tools.length === 0 && <div style={SD.empty}>(no tools called)</div>}
        {tools.map((t, i) => {
          // Sprint 2: the batch runner persists { name, input, result, at, t_ms }
          // verbatim. Always render the arguments — an EMPTY object is the
          // defect we most want to see (Nova "searching" with getProvidersList({})).
          const args = t.input ?? t.arguments ?? t.params ?? null;
          const hasArgs = !!args && typeof args === 'object' && Object.keys(args).length > 0;
          const when = t.at || t.timestamp || null;
          const whenLabel = when ? (typeof when === 'number' ? new Date(when) : new Date(when)).toLocaleTimeString() : null;
          const offset = (t.t_ms !== undefined && t.t_ms !== null) ? `+${(t.t_ms / 1000).toFixed(1)}s` : null;
          return (
            <div key={i} style={SD.tool}>
              <div style={SD.toolHead}>
                <b>#{i + 1} {t.toolName || t.name || 'unknown'}</b>
                <span style={SD.toolMeta}>{[whenLabel, offset].filter(Boolean).join(' · ') || '(no timestamp)'}</span>
              </div>
              <pre style={{ ...SD.pre, ...(hasArgs ? {} : SD.preWarn) }}>
                args: {hasArgs ? JSON.stringify(args, null, 2) : '{}   ← called with NO arguments'}
              </pre>
              {t.result && <pre style={SD.pre}>result: {JSON.stringify(t.result, null, 2).slice(0, 500)}</pre>}
            </div>
          );
        })}
      </div>

      {expected.length > 0 && (
        <div style={SD.section}>
          <div style={SD.h4}>Expected tools (from scenario definition)</div>
          <div style={SD.mono}>{JSON.stringify(expected)}</div>
        </div>
      )}

      <div style={SD.section}>
        <div style={SD.h4}>Judge evaluation</div>
        <div style={SD.eval}>
          <div><b>Verdict:</b> {sc.verdict} · <b>Score:</b> {sc.score ?? '-'}</div>
          {sc.evaluation_notes && <div style={SD.notes}>{sc.evaluation_notes}</div>}
          {!sc.evaluation_notes && <div style={SD.empty}>(no judge notes)</div>}
        </div>
      </div>

      {sc.error && (
        <div style={SD.section}>
          <div style={SD.h4}>Error</div>
          <pre style={{ ...SD.pre, color: '#991b1b' }}>{sc.error}</pre>
        </div>
      )}
    </div>
  );
}

const SD = {
  wrap: { padding: '12px 16px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, marginTop: 8, maxHeight: '75vh', overflowY: 'auto' },
  section: { marginBottom: 16 },
  h4: { fontWeight: 600, marginBottom: 6, color: '#1f2937', fontSize: 13, borderBottom: '1px solid #f3f4f6', paddingBottom: 4 },
  empty: { color: '#9ca3af', fontStyle: 'italic', fontSize: 12 },
  transcript: { maxHeight: 320, overflowY: 'auto', border: '1px solid #f3f4f6', borderRadius: 4, padding: 8 },
  turn: { padding: '6px 10px', marginBottom: 4, borderRadius: 4, fontSize: 13 },
  turnAssistant: { background: '#eff6ff', borderLeft: '3px solid #2563eb' },
  turnUser: { background: '#f9fafb', borderLeft: '3px solid #9ca3af' },
  turnRole: { fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 2 },
  turnText: { color: '#111827', whiteSpace: 'pre-wrap' },
  tool: { padding: 8, marginBottom: 6, background: '#fefce8', borderRadius: 4, fontSize: 12 },
  toolHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 },
  toolMeta: { fontFamily: 'monospace', fontSize: 11, color: '#6b7280' },
  pre: { margin: '4px 0 0', fontSize: 11, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#fff', padding: 6, borderRadius: 3 },
  preWarn: { background: '#fee2e2', color: '#991b1b' },
  mono: { fontFamily: 'monospace', fontSize: 11, color: '#4b5563' },
  eval: { padding: 8, background: '#f9fafb', borderRadius: 4, fontSize: 13 },
  notes: { marginTop: 4, color: '#374151', fontStyle: 'italic' },
};

function statusStyle(s) {
  const base = { padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 };
  if (s === 'completed') return { ...base, background: '#dcfce7', color: '#15803d' };
  if (s === 'running' || s === 'pending') return { ...base, background: '#dbeafe', color: '#1e40af' };
  if (s === 'cancelled' || s === 'force_killed') return { ...base, background: '#fef3c7', color: '#92400e' };
  return { ...base, background: '#fee2e2', color: '#991b1b' };
}

function verdictStyle(v) {
  const base = { padding: '2px 6px', borderRadius: 3, fontSize: 11 };
  if (v === 'PASS') return { ...base, background: '#dcfce7', color: '#15803d' };
  if (v === 'PARTIAL') return { ...base, background: '#fef3c7', color: '#92400e' };
  if (v === 'FAIL') return { ...base, background: '#fee2e2', color: '#991b1b' };
  return { ...base, background: '#e5e7eb', color: '#374151' };
}

const S = {
  container: { display: 'flex', flexDirection: 'column', gap: 12 },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  h3: { margin: 0, color: '#111827', fontSize: 18 },
  table: { width: '100%', borderCollapse: 'collapse', background: 'white' },
  th: { padding: '8px 10px', textAlign: 'left', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 12, fontWeight: 600 },
  td: { padding: '8px 10px', borderBottom: '1px solid #f3f4f6', fontSize: 13 },
  tdExpand: { padding: '12px 20px', background: '#f9fafb' },
  innerTable: { width: '100%', marginTop: 8 },
  btnSecondary: { background: '#e5e7eb', color: '#111827', border: 'none', padding: '6px 12px', borderRadius: 4, cursor: 'pointer' },
  btnSmall: { background: '#e5e7eb', color: '#111827', border: 'none', padding: '3px 10px', borderRadius: 3, cursor: 'pointer', fontSize: 12, marginRight: 4 },
  tdDetail: { padding: '0 20px 12px', background: '#f9fafb' },
  livePill: { background: '#fee2e2', color: '#991b1b', padding: '1px 6px', borderRadius: 3, fontSize: 11, fontWeight: 600 },
  empty: { color: '#6b7280', fontStyle: 'italic', padding: 20, textAlign: 'center' },
  err: { background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 4, fontSize: 14 },
  warnBanner: { background: '#fef3c7', color: '#92400e', padding: 10, borderRadius: 4, fontSize: 14 },
};

export default HistoryTab;
